import {
  getAcknowledgedCareAlertsInAcknowledgementWindow,
  getCareAlertsCreatedInRange,
} from './careAlertService';
import { median } from '../utils/reportFormat';

const LEVEL_LABEL = {
  1: 'Level 1 · Low',
  2: 'Level 2 · Moderate',
  3: 'Level 3 · High',
  4: 'Level 4 · Critical',
};

/** Fetch both cohorts (created-in-range + acknowledged-in-window) for the report. */
export async function fetchCareAlertsReportData(tenantId, patientIds, start, end) {
  if (!tenantId || !patientIds?.length || !start || !end) {
    return { ackRows: [], createdCohort: [] };
  }
  const [ackRows, createdCohort] = await Promise.all([
    getAcknowledgedCareAlertsInAcknowledgementWindow(tenantId, patientIds, start, end, 500),
    getCareAlertsCreatedInRange(tenantId, patientIds, start, end, 500),
  ]);
  return { ackRows, createdCohort };
}

function ms(v) {
  if (v == null) return NaN;
  if (typeof v.getTime === 'function') return v.getTime();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? NaN : d.getTime();
}

/**
 * Build the printable Care Alerts dataset.
 * @param {{ ackRows: object[], createdCohort: object[] }} data
 * An alert counts as resolved only when it was resolved by `periodEnd`, so later
 * resolutions never change a past period.
 * @param {{ excludeHistorical?: boolean, userNameById?: Map<string,string>, periodEnd?: Date }} opts
 */
export function buildCareAlertsReport(data, opts = {}) {
  const { excludeHistorical = true, userNameById = new Map(), periodEnd = null } = opts;
  const endMs = periodEnd ? ms(periodEnd) : Infinity;
  const ackAll = data?.ackRows || [];
  const cohortAll = data?.createdCohort || [];

  const ackRows = excludeHistorical ? ackAll.filter((r) => !r.stale) : ackAll;
  const cohort = excludeHistorical ? cohortAll.filter((a) => !a.stale) : cohortAll;
  const excludedAckCount = ackAll.length - ackRows.length;
  const excludedCohortCount = cohortAll.length - cohort.length;

  // Acknowledgement deltas (create -> ack).
  const ackDeltas = [];
  ackRows.forEach((r) => {
    const c = ms(r.createdAt);
    const a = ms(r.acknowledgedAt);
    if (Number.isFinite(c) && Number.isFinite(a) && a - c >= 0) ackDeltas.push(a - c);
  });

  // Resolution deltas on the created cohort.
  const resolveDeltas = [];
  let resolvedCount = 0;
  cohort.forEach((r) => {
    if (r.status !== 'resolved') return;
    const x = ms(r.resolvedAt);
    if (Number.isFinite(endMs) && !(Number.isFinite(x) && x <= endMs)) return;
    resolvedCount += 1;
    const c = ms(r.createdAt);
    if (Number.isFinite(c) && Number.isFinite(x) && x - c >= 0) resolveDeltas.push(x - c);
  });

  // Distribution by level (on the created cohort).
  const levelCounts = new Map();
  cohort.forEach((a) => {
    const lv = Number(a.level) || 0;
    levelCounts.set(lv, (levelCounts.get(lv) || 0) + 1);
  });
  const levelDistribution = [...levelCounts.entries()]
    .sort((a, b) => b[0] - a[0])
    .map(([level, count]) => ({
      level,
      label: LEVEL_LABEL[level] || (level ? `Level ${level}` : 'Unspecified'),
      count,
    }));

  // By pathway name (top 8 on the created cohort).
  const pathwayCounts = new Map();
  cohort.forEach((a) => {
    const name = (a.pathwayName || a.title || 'Unspecified').trim() || 'Unspecified';
    pathwayCounts.set(name, (pathwayCounts.get(name) || 0) + 1);
  });
  const byPathway = [...pathwayCounts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // By source.
  const sourceCounts = new Map();
  cohort.forEach((a) => {
    const s = (a.source || 'Unspecified').trim() || 'Unspecified';
    sourceCounts.set(s, (sourceCounts.get(s) || 0) + 1);
  });
  const bySource = [...sourceCounts.entries()]
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count);

  // By acknowledging user.
  const ackByUser = new Map();
  ackRows.forEach((r) => {
    const uid = r.acknowledgedBy || '—';
    const c = ms(r.createdAt);
    const a = ms(r.acknowledgedAt);
    if (!Number.isFinite(c) || !Number.isFinite(a) || a - c < 0) return;
    if (!ackByUser.has(uid)) ackByUser.set(uid, []);
    ackByUser.get(uid).push(a - c);
  });
  const byAcknowledger = [];
  ackByUser.forEach((arr, uid) => {
    byAcknowledger.push({
      uid,
      label: uid === '—' ? '—' : userNameById.get(uid) || uid,
      count: arr.length,
      medianMs: median(arr),
    });
  });
  byAcknowledger.sort((a, b) => b.count - a.count);

  const created = cohort.length;
  return {
    kpis: {
      created,
      resolved: resolvedCount,
      resolvedPct: created ? (100 * resolvedCount) / created : null,
      medianResolveMs: median(resolveDeltas),
      ackCount: ackRows.length,
      medianAckMs: median(ackDeltas),
    },
    levelDistribution,
    byPathway,
    bySource,
    byAcknowledger,
    excludedAckCount,
    excludedCohortCount,
    visibleAckRows: ackRows,
  };
}
